import { ChangeDetectionStrategy, Component } from '@angular/core';

import { AppLanguage, LanguageService } from '../../providers/language/language.service';

interface LegalSection {
    heading?: string;
    paras?: string[];
    bullets?: string[];
    // Optional trailing paragraphs rendered after the bullet list.
    trailing?: string[];
}

interface LegalPage {
    title: string;
    sections: LegalSection[];
}

@Component({
    selector: 'vsf-technical-disclaimer-page',
    templateUrl: './technical-disclaimer-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TechnicalDisclaimerPageComponent {
    constructor(private languageService: LanguageService) {}

    get page(): LegalPage {
        return this.content[this.languageService.currentLanguage];
    }

    private content: Record<AppLanguage, LegalPage> = {
        ar: {
            title: 'إخلاء المسؤولية الفنية',
            sections: [
                {
                    heading: 'مقدمة',
                    paras: [
                        'توفر Industore منتجات ومعدات وقطع صناعية وتقنية مخصصة للاستخدامات المهنية والصناعية والتجارية.',
                        'وبسبب الطبيعة الفنية لهذه المنتجات، يرجى قراءة إخلاء المسؤولية هذا بعناية قبل الشراء أو الاستخدام.',
                    ],
                },
                {
                    heading: '1. مسؤولية اختيار المنتج',
                    paras: [
                        'يتحمل العميل المسؤولية الكاملة عن التأكد من أن المنتج مناسب للتطبيق أو الاستخدام المطلوب قبل إتمام عملية الشراء.',
                        'وتنصح Industore العملاء بمراجعة المواصفات الفنية ومتطلبات التشغيل بدقة قبل الاستخدام.',
                    ],
                },
                {
                    heading: '2. المعلومات والمواصفات الفنية',
                    paras: ['تبذل Industore جهدًا لتوفير معلومات ومواصفات دقيقة، إلا أن بعض المعلومات قد تتغير نتيجة:'],
                    bullets: [
                        'تحديثات الشركات المصنعة',
                        'اختلاف دفعات الإنتاج',
                        'التعديلات الفنية من الموردين',
                    ],
                    trailing: ['وقد تختلف بعض الصور أو المواصفات عن المنتج الفعلي.'],
                },
                {
                    heading: '3. الاستخدام الفني والصناعي',
                    paras: [
                        'يجب استخدام المنتجات وفقًا للتعليمات الفنية وظروف التشغيل المناسبة.',
                        'ولا تتحمل Industore أي مسؤولية عن الأضرار الناتجة عن:',
                    ],
                    bullets: [
                        'الاستخدام غير الصحيح',
                        'التركيب الخاطئ',
                        'التشغيل غير المطابق للمواصفات',
                        'سوء التخزين أو الصيانة',
                    ],
                },
                {
                    heading: '4. التركيب والتشغيل',
                    paras: [
                        'بعض المنتجات قد تتطلب تركيبًا أو تشغيلًا بواسطة فنيين أو جهات مختصة.',
                        'وتوصي Industore بالاعتماد على مختصين مؤهلين عند التعامل مع المنتجات الصناعية أو الكهربائية أو الميكانيكية.',
                    ],
                },
                {
                    heading: '5. حدود المسؤولية الفنية',
                    paras: ['لا تتحمل Industore أي مسؤولية عن:'],
                    bullets: [
                        'خسائر الإنتاج',
                        'توقف الأعمال',
                        'الأضرار غير المباشرة',
                        'الأعطال الناتجة عن الاستخدام غير الصحيح',
                        'عدم توافق المنتج مع التطبيق المطلوب',
                    ],
                },
                {
                    heading: '6. مسؤولية الفحص قبل التشغيل',
                    paras: [
                        'يجب على العميل فحص المنتج والتأكد من سلامته وصحة تركيبه قبل التشغيل النهائي أو دمجه ضمن أي نظام صناعي أو تشغيلي.',
                    ],
                },
                {
                    heading: '7. المنتجات الصناعية الحساسة',
                    paras: [
                        'قد تتطلب بعض المنتجات ظروف نقل أو تخزين أو تشغيل خاصة.',
                        'ويتحمل العميل مسؤولية الالتزام بالمتطلبات الفنية الخاصة بكل منتج.',
                    ],
                },
                {
                    heading: '8. الاستشارات الفنية',
                    paras: [
                        'أي توصيات أو معلومات فنية مقدمة من Industore تعتبر معلومات إرشادية عامة، ولا تغني عن التقييم الفني المباشر للتطبيق الفعلي من قبل العميل أو المختصين المسؤولين عنه.',
                    ],
                },
                {
                    heading: '9. التعديلات والتحديثات',
                    paras: [
                        'تحتفظ Industore بحق تعديل المعلومات الفنية أو المواصفات أو المحتوى المرتبط بالمنتجات في أي وقت دون إشعار مسبق.',
                    ],
                },
            ],
        },
        en: {
            title: 'Technical Disclaimer',
            sections: [
                {
                    heading: 'Introduction',
                    paras: [
                        'Industore provides industrial and technical products, equipment, and parts intended for professional, industrial, and commercial use.',
                        'Due to the technical nature of these products, please read this disclaimer carefully before purchase or use.',
                    ],
                },
                {
                    heading: '1. Product Selection Responsibility',
                    paras: [
                        'The customer bears full responsibility for ensuring that the product is suitable for the intended application or use before completing the purchase.',
                        'Industore advises customers to carefully review the technical specifications and operating requirements before use.',
                    ],
                },
                {
                    heading: '2. Technical Information and Specifications',
                    paras: ['Industore makes an effort to provide accurate information and specifications; however, some information may change as a result of:'],
                    bullets: [
                        'Manufacturer updates',
                        'Variations between production batches',
                        'Technical modifications from suppliers',
                    ],
                    trailing: ['Some images or specifications may differ from the actual product.'],
                },
                {
                    heading: '3. Technical and Industrial Use',
                    paras: [
                        'Products must be used in accordance with the technical instructions and appropriate operating conditions.',
                        'Industore bears no responsibility for damages resulting from:',
                    ],
                    bullets: [
                        'Incorrect use',
                        'Incorrect installation',
                        'Operation not conforming to specifications',
                        'Poor storage or maintenance',
                    ],
                },
                {
                    heading: '4. Installation and Operation',
                    paras: [
                        'Some products may require installation or operation by technicians or specialized parties.',
                        'Industore recommends relying on qualified specialists when dealing with industrial, electrical, or mechanical products.',
                    ],
                },
                {
                    heading: '5. Limits of Technical Liability',
                    paras: ['Industore bears no responsibility for:'],
                    bullets: [
                        'Production losses',
                        'Business interruption',
                        'Indirect damages',
                        'Malfunctions resulting from incorrect use',
                        'Product incompatibility with the intended application',
                    ],
                },
                {
                    heading: '6. Pre-Operation Inspection Responsibility',
                    paras: [
                        'The customer must inspect the product and verify its integrity and correct installation before final operation or integration into any industrial or operational system.',
                    ],
                },
                {
                    heading: '7. Sensitive Industrial Products',
                    paras: [
                        'Some products may require special transport, storage, or operating conditions.',
                        'The customer bears responsibility for complying with the technical requirements specific to each product.',
                    ],
                },
                {
                    heading: '8. Technical Consultations',
                    paras: [
                        'Any recommendations or technical information provided by Industore are considered general guidance and do not replace a direct technical assessment of the actual application by the customer or the specialists responsible for it.',
                    ],
                },
                {
                    heading: '9. Modifications and Updates',
                    paras: [
                        'Industore reserves the right to modify the technical information, specifications, or product-related content at any time without prior notice.',
                    ],
                },
            ],
        },
    };
}
